import { base64url, fromBase64url, utf8 } from './encoding';

/** A business signing key. `notAfter` bounds how long a stolen copy stays useful. */
export interface BusinessKey {
  kid: string; // [A-Za-z0-9_-]+, no dots
  secret: string; // base64url, >= 32 random bytes
  notBefore: number; // epoch ms
  notAfter: number; // epoch ms
}

/** Downloaded by kiosks / used by the server. Overlap keys across rotation. */
export interface KeySet {
  keys: BusinessKey[];
}

/** What the member app stores (derived server-side; the business key never leaves server/kiosk). */
export interface MemberCredential {
  kid: string;
  memberId: string;
  secret: string; // base64url, HKDF(businessKey, memberId)
  notAfter: number;
}

export const ID_RE = /^[A-Za-z0-9_-]+$/;
export function assertId(label: string, v: string): void {
  if (!ID_RE.test(v)) throw new Error(`invalid ${label}: ${JSON.stringify(v)}`);
}

const SALT = utf8('helmr-checkin-v1');

async function hkdf(secretB64: string, info: string): Promise<string> {
  const base = await crypto.subtle.importKey('raw', fromBase64url(secretB64), 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits({ name: 'HKDF', hash: 'SHA-256', salt: SALT, info: utf8(info) }, base, 256);
  return base64url(new Uint8Array(bits));
}

export function deriveMemberSecret(key: BusinessKey, memberId: string): Promise<string> {
  assertId('memberId', memberId);
  return hkdf(key.secret, `member|${memberId}`);
}

export function deriveKioskSecret(key: BusinessKey, branchId: string, kioskId: string): Promise<string> {
  assertId('branchId', branchId);
  assertId('kioskId', kioskId);
  return hkdf(key.secret, `kiosk|${branchId}|${kioskId}`);
}

export async function issueMemberCredential(key: BusinessKey, memberId: string): Promise<MemberCredential> {
  return { kid: key.kid, memberId, secret: await deriveMemberSecret(key, memberId), notAfter: key.notAfter };
}

export async function hmac(secretB64: string, message: string, bytes: number): Promise<Uint8Array> {
  const k = await crypto.subtle.importKey('raw', fromBase64url(secretB64), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const sig = await crypto.subtle.sign('HMAC', k, utf8(message));
  return new Uint8Array(sig).slice(0, bytes);
}

export function generateBusinessKey(kid: string, notBefore: number, notAfter: number): BusinessKey {
  assertId('kid', kid);
  const raw = crypto.getRandomValues(new Uint8Array(32));
  return { kid, secret: base64url(raw), notBefore, notAfter };
}

/** The key with this id usable at `atMs` (inside its validity range). Survives a kid being reused. */
export function findKey(set: KeySet, kid: string, atMs: number): BusinessKey | undefined {
  return set.keys.find((x) => x.kid === kid && atMs >= x.notBefore && atMs <= x.notAfter);
}
